import { useBackupStatus, useDatabaseStatus } from '../../api/queries'
import { Card, ErrorBox, Loading } from '../../components/ui'

/** "127 KB" - filstørrelser skal kunne læses uden at tælle nuller. */
function formatBytes(bytes: number) {
  if (bytes <= 0) return '-'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** "for 8 timer siden" - hvor gammel kopien er, er det man skal se først. */
function formatAge(hours?: number | null) {
  if (hours === null || hours === undefined) return 'aldrig'
  if (hours < 1) return 'inden for den seneste time'
  if (hours < 24) return `for ${Math.round(hours)} ${Math.round(hours) === 1 ? 'time' : 'timer'} siden`
  const days = Math.round(hours / 24)
  return `for ${days} ${days === 1 ? 'dag' : 'dage'} siden`
}

function formatDateTime(value?: string | null) {
  if (!value) return '-'
  return new Date(value).toLocaleString('da-DK', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * Drift: det man skal kunne se uden at logge ind på serveren.
 *
 * Siden findes først og fremmest for sikkerhedskopien. Den tages af et
 * systemd-job om natten, og et job, ingen kigger på, opdager man først er holdt
 * op med at køre, den dag man skal bruge det. Derfor står alderen øverst og med
 * store bogstaver, når den er for gammel.
 */
export function OperationsPage() {
  const backup = useBackupStatus()
  const database = useDatabaseStatus()

  return (
    <div className="stack" style={{ gap: 16 }}>
      <Card title="Sikkerhedskopi af databasen">
        {backup.isLoading && <Loading />}
        {backup.error && <ErrorBox error={backup.error} />}

        {backup.data && (
          <>
            {/* Alderen er det vigtigste. En kopi fra i nat er fin; en fra i
                sidste uge betyder, at jobbet er gået i stå. */}
            {backup.data.isStale ? (
              <div className="error-box" style={{ marginBottom: 12 }}>
                <strong>Der ligger ikke en frisk sikkerhedskopi.</strong>{' '}
                {backup.data.message ?? 'Se journalen for tcpss-backup på serveren.'}
              </div>
            ) : (
              <div className="small" style={{ marginBottom: 12 }}>
                <strong>Seneste kopi taget {formatAge(backup.data.ageHours)}.</strong>{' '}
                {backup.data.message}
              </div>
            )}

            <div className="form-grid">
              <div>
                <div className="tiny muted">Kørte</div>
                <div className="small">{formatDateTime(backup.data.ranAt)}</div>
              </div>
              <div>
                <div className="tiny muted">Fil</div>
                <div className="small">{backup.data.file ?? '-'}</div>
              </div>
              <div>
                <div className="tiny muted">Størrelse</div>
                <div className="small">{formatBytes(backup.data.bytes)}</div>
              </div>
            </div>

            <p className="tiny muted" style={{ marginTop: 12, marginBottom: 0 }}>
              Kopien tages hver nat kl. 02.30 og gemmes i 30 dage. Filen kontrolleres bagefter -
              både at den kan pakkes ud, og at der står tabeller i den; en tom fil ser i en
              mappevisning ud præcis som en rigtig.
            </p>
            <p className="tiny muted" style={{ marginTop: 6, marginBottom: 0 }}>
              <strong>Bemærk:</strong> kopierne ligger på samme maskine som databasen. Det
              beskytter mod en forkert SQL-sætning eller en slettet tabel - men ikke mod at
              serveren går tabt. Hent dem ned med jævne mellemrum.
            </p>
          </>
        )}
      </Card>

      <Card title="Database">
        {database.isLoading && <Loading />}
        {database.error && <ErrorBox error={database.error} />}

        {database.data && (
          database.data.ok ? (
            <div className="small">
              Databasen svarer på {Math.round(database.data.responseMs)} ms.{' '}
              <span className="muted">{database.data.users} brugere i alt.</span>
            </div>
          ) : (
            <div className="error-box">
              Databasen svarer ikke: {database.data.error}
            </div>
          )
        )}
      </Card>
    </div>
  )
}
